import ipaddr from 'ipaddr.js';

const IPV4_COMPATIBLE = ipaddr.IPv6.parse('::');

/**
 * Returns true when the address is a public IPv4/IPv6 unicast address.
 *
 * ipaddr.js calls an IPv4-compatible address (::/96) `unicast`, but a dual-stack host may route
 * `::7f00:1` to 127.0.0.1. Such an address is judged by the IPv4 address in its last 32 bits.
 * `::` and `::1` keep their own ranges. ipaddr.js already gives IPv4-mapped (::ffff:0:0/96),
 * NAT64, 6to4 and Teredo addresses a non-unicast range, so they stay refused.
 */
export function isPublicUnicastIp(raw: string): boolean {
  try {
    const addr = ipaddr.parse(raw);
    if (addr.kind() === 'ipv6' && addr.range() === 'unicast' && (addr as ipaddr.IPv6).match(IPV4_COMPATIBLE, 96)) {
      const bytes = addr.toByteArray().slice(12);
      return ipaddr.fromByteArray(bytes).range() === 'unicast';
    }
    return addr.range() === 'unicast';
  } catch {
    return false;
  }
}

/** Returns true when the address is an IP literal that must not be dialed. */
export function isUnsafeIpLiteral(raw: string): boolean {
  const host = raw.trim();
  if (!host) return true;
  return ipaddr.isValid(host) && !isPublicUnicastIp(host);
}

/**
 * Normalize an IPv4 literal to canonical dotted-decimal form, or null when the
 * host is not a recognizable IPv4 encoding (hostname, invalid, or shorthand).
 */
export function normalizeIPv4Literal(host: string): string | null {
  const h = host.trim();
  if (!h) return null;

  try {
    return ipaddr.IPv4.parse(h).toString();
  } catch {
    return null;
  }
}
